import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["circleSize", "direction", "responsiveBreakpoint", "onStepClick", "stepIndex", "stepNames", "StepIconComponent", "use", "aria-label"];
import { useRef, useState, useEffect, useCallback, useLayoutEffect } from 'react';
import styled from 'foundations-theming/utils';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import StepIndicatorSteps from './StepIndicatorSteps';
import { useWizardHeaderContext } from '../Wizard/WizardHeaderProvider';
import { deepMerge } from '../../utils/deepMerge/deepMerge';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledStepIndicatorWrapper = styled.div.withConfig({
  displayName: "StepIndicator__StyledStepIndicatorWrapper",
  componentId: "sc-84g00q-0"
})(({
  $direction,
  $cssStyles,
  theme
}) => {
  return Object.assign({
    display: 'inline-flex',
    flex: '1 1 auto',
    flexWrap: 'wrap',
    marginLeft: theme.space['50'],
    marginTop: theme.space['50'],
    alignItems: $direction === 'horizontal' ? 'flex-start' : undefined,
    justifyContent: $direction === 'horizontal' ? 'space-between' : undefined,
    alignSelf: $direction === 'vertical' ? 'stretch' : undefined,
    flexDirection: $direction === 'vertical' ? 'column' : undefined
  }, $cssStyles);
});

// This is for the single status label usage
const StyledStatusLabel = styled.span.withConfig({
  displayName: "StepIndicator__StyledStatusLabel",
  componentId: "sc-84g00q-1"
})(({
  $use,
  theme,
  $styleOverrides
}) => {
  return deepMerge(Object.assign({
    display: 'none',
    flexBasis: '100%',
    textAlign: 'center'
  }, ($use === 'compact' || $use === 'flush') && Object.assign({}, theme.typography['detail-200'], {
    display: 'block',
    color: theme.color['text-core-default'],
    textAlign: 'center',
    textTransform: 'uppercase',
    wordWrap: 'break-word',
    '& small': {
      fontSmoothing: 'auto',
      textTransform: 'none'
    }
  })), $styleOverrides);
});
function StepIndicator(_ref) {
  var _wizardHeaderContext$;
  let {
      circleSize = '100',
      direction = 'horizontal',
      responsiveBreakpoint = 500,
      onStepClick,
      stepIndex,
      stepNames,
      StepIconComponent,
      use = 'default',
      'aria-label': ariaLabel
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const wizardHeaderContext = useWizardHeaderContext();
  const [isWidthConstrained, setIsWidthConstrained] = useState(false);
  const stepRef = useRef(null);
  const currentUse = isWidthConstrained ? 'compact' : use;
  const measureWidth = useCallback(() => {
    var _stepRef$current;
    if (use === 'compact' || direction !== 'horizontal' || !stepRef.current) return;

    // Measure width of wrapper and compare against threshold
    const isNowConstrained = ((_stepRef$current = stepRef.current) === null || _stepRef$current === void 0 ? void 0 : _stepRef$current.clientWidth) < responsiveBreakpoint;
    if (isNowConstrained !== isWidthConstrained) {
      setIsWidthConstrained(isNowConstrained);
    }
  }, [direction, isWidthConstrained, responsiveBreakpoint, use]);

  // we want to ensure that on mount this also gets called + set
  useLayoutEffect(() => {
    measureWidth();
  }, [measureWidth]);
  useEffect(() => {
    window.addEventListener('resize', measureWidth);
    return () => {
      window.removeEventListener('resize', measureWidth);
    };
  }, [measureWidth]);
  return /*#__PURE__*/_jsxs(StyledStepIndicatorWrapper, Object.assign({}, domProps, {
    $cssStyles: cssStyles,
    ref: stepRef,
    role: "list",
    "aria-label": ariaLabel,
    $direction: direction,
    children: [/*#__PURE__*/_jsx(StepIndicatorSteps, {
      circleSize: circleSize,
      direction: direction,
      onStepClick: onStepClick,
      stepIndex: stepIndex,
      stepNames: stepNames,
      StepIconComponent: StepIconComponent,
      use: currentUse,
      isCustomized: !!StepIconComponent
    }), direction === 'horizontal' ? /*#__PURE__*/_jsx(StyledStatusLabel, {
      $use: currentUse,
      "aria-hidden": true,
      $styleOverrides: (_wizardHeaderContext$ = wizardHeaderContext === null || wizardHeaderContext === void 0 ? void 0 : wizardHeaderContext.stepIndicatorStyleOverrides) !== null && _wizardHeaderContext$ !== void 0 ? _wizardHeaderContext$ : {},
      children: stepNames[stepIndex]
    }) : null]
  }));
}
export { StepIndicator };